import React, { useState, useEffect } from "react";
import moment from "moment";
import { getGoogleLeads } from "@/actions/reports";
import { Download, Chrome, BarChart3, Users, Target, Activity, TrendingUp } from "lucide-react";
import ReportHeader from "../components/ReportHeader";

interface GoogleLeadsData {
  summary: {
    total_leads: number;
    total_campaigns: number;
    total_sources: number;
    total_mediums: number;
    avg_leads_per_campaign: string;
    avg_leads_per_source: string;
    avg_leads_per_medium: string;
    top_campaign: { campaign_name: string; total_leads: number } | null;
    top_source: { source_name: string; total_leads: number } | null;
    top_medium: { medium_name: string; total_leads: number } | null;
  };
  breakdowns: {
    campaigns: Array<{ campaign_name: string; total_leads: number; source_count: number; medium_count: number; percentage: string }>;
    sources: Array<{ source_name: string; total_leads: number; campaign_count: number; percentage: string }>;
    mediums: Array<{ medium_name: string; total_leads: number; campaign_count: number; percentage: string }>;
  };
}

const formatNumber = (num: number): string => num?.toLocaleString() || "0";

const GoogleReport: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [googleData, setGoogleData] = useState<GoogleLeadsData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getGoogleLeads({ from_date: fromDate, to_date: toDate });
      if (response.success) setGoogleData(response.data as GoogleLeadsData);
    } catch (error) {
      console.error("Error fetching Google data:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportData = (data: any[], filename: string) => {
    if (!data?.length) return;
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(",")];
    data.forEach((row) => csvRows.push(headers.map((h) => `"${row[h] || ""}"`).join(",")));
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", `${filename}-${fromDate}_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="h-full">
        <ReportHeader title="Google Leads" subtitle="UTM campaign analysis" icon={<Chrome className="h-5 w-5 text-white" />} fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} onApplyFilter={fetchData} />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ReportHeader title="Google Leads" subtitle="UTM campaign analysis" icon={<Chrome className="h-5 w-5 text-white" />} fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} onApplyFilter={fetchData} />

      <div className="flex-1 overflow-y-auto p-6">
        {!googleData ? (
          <div className="text-center py-8">
            <Chrome className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No Google data available for the selected date range</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-brandColor">
                <div className="flex justify-between items-center">
                  <div><div className="text-sm gray-text">Total Leads</div><div className="text-2xl font-bold">{formatNumber(googleData.summary.total_leads)}</div></div>
                  <Users className="h-8 w-8 text-brandColor" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-green-500">
                <div className="flex justify-between items-center">
                  <div><div className="text-sm gray-text">Campaigns</div><div className="text-2xl font-bold">{formatNumber(googleData.summary.total_campaigns)}</div></div>
                  <Target className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-purple-500">
                <div className="flex justify-between items-center">
                  <div><div className="text-sm gray-text">Sources</div><div className="text-2xl font-bold">{formatNumber(googleData.summary.total_sources)}</div></div>
                  <BarChart3 className="h-8 w-8 text-purple-500" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-blue-500">
                <div className="flex justify-between items-center">
                  <div><div className="text-sm gray-text">Mediums</div><div className="text-2xl font-bold">{formatNumber(googleData.summary.total_mediums)}</div></div>
                  <Activity className="h-8 w-8 text-blue-500" />
                </div>
              </div>
            </div>

            {/* Averages */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="background-container rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div><div className="text-sm gray-text">Avg Leads/Campaign</div><div className="text-xl font-bold">{googleData.summary.avg_leads_per_campaign}</div></div>
                  <TrendingUp className="h-6 w-6 text-brandColor" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div><div className="text-sm gray-text">Avg Leads/Source</div><div className="text-xl font-bold">{googleData.summary.avg_leads_per_source}</div></div>
                  <TrendingUp className="h-6 w-6 text-green-500" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div><div className="text-sm gray-text">Avg Leads/Medium</div><div className="text-xl font-bold">{googleData.summary.avg_leads_per_medium}</div></div>
                  <TrendingUp className="h-6 w-6 text-purple-500" />
                </div>
              </div>
            </div>

            {/* Campaign Breakdown */}
            {googleData.breakdowns?.campaigns?.length > 0 && (
              <div className="background-container rounded-lg shadow">
                <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 rounded-t-lg flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Target className="h-5 w-5" />Campaign Breakdown</h3>
                  <button onClick={() => exportData(googleData.breakdowns.campaigns, "google-campaigns")} className="inline-flex items-center gap-2 px-4 py-2 bg-white text-green-600 text-sm font-medium rounded-lg hover:bg-gray-100"><Download className="h-4 w-4" />Export</button>
                </div>
                <div className="p-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sources</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mediums</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">% of Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {googleData.breakdowns.campaigns.map((c, i) => (
                        <tr key={i}>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{c.campaign_name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{formatNumber(c.total_leads)}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{c.source_count}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{c.medium_count}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{c.percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Source Breakdown */}
            {googleData.breakdowns?.sources?.length > 0 && (
              <div className="background-container rounded-lg shadow">
                <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 rounded-t-lg flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2"><BarChart3 className="h-5 w-5" />Source Breakdown</h3>
                  <button onClick={() => exportData(googleData.breakdowns.sources, "google-sources")} className="inline-flex items-center gap-2 px-4 py-2 bg-white text-purple-600 text-sm font-medium rounded-lg hover:bg-gray-100"><Download className="h-4 w-4" />Export</button>
                </div>
                <div className="p-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaigns</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">% of Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {googleData.breakdowns.sources.map((s, i) => (
                        <tr key={i}>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{s.source_name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{formatNumber(s.total_leads)}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{s.campaign_count}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{s.percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleReport;
